import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import {
  submitToHubspot,
  validateField,
} from "../Products/MidstreamContactForm";
import "./HomeV2.scss";

const IMG = `${process.env.PUBLIC_URL}/assets/img/home`;

const NAV_LINKS = [
  { label: "Midstream", to: "/midstream" },
  { label: "Energy Transition", to: "/#choose-your-path", disabled: true },
  { label: "Upstream", to: "/#choose-your-path", disabled: true },
  { label: "Insights", to: "/news", disabled: true },
  { label: "Careers", to: "/careers", disabled: true },
  { label: "Contacts", to: "/#contact" },
];

const JOURNEY = [
  { label: "Wellhead", tone: "teal-alt", line: "solid" },
  { label: "Gathering", tone: "teal", line: "solid" },
  { label: "Plant", tone: "teal", line: "gradient" },
  { label: "Pipeline", tone: "amber", line: "gradient" },
  { label: "Settlement", tone: "amber" },
];

const PILLARS = [
  {
    icon: "icon-physics.svg",
    width: 20,
    height: 23,
    title: "Physics behind every number.",
    titleWidth: 284,
    body: "Engineers can inspect and change every assumption.",
  },
  {
    icon: "icon-dollar.svg",
    width: 22,
    height: 22,
    title: "Every decision priced in dollars.",
    titleWidth: 308,
    body: "With the math behind it, ready for the board, a lender, or an auditor.",
  },
  {
    icon: "icon-agents.svg",
    width: 23,
    height: 23,
    title: "Agents that do the legwork.",
    titleWidth: 262,
    body: "They run scenarios against your data and flag what changed.",
  },
];

const PATHS = [
  {
    title: "Midstream.",
    image: "path-midstream.png",
    imageClass: "hv2-shot--crop",
    body: "You're leaving money on the table. We can tell you how much.",
    cta: "See Dsider for Midstream",
    to: "/midstream",
    primary: true,
  },
  {
    title: "Energy transition.",
    image: "path-energy-transition.png",
    body: "Price every design, incentive, and offtake choice before you commit capital.",
    cta: "Explore energy transition →",
    to: "/#contact",
    disabled: true,
  },
  {
    title: "Upstream.",
    image: "path-upstream.png",
    body: "Know the true value of your offshore asset and optimize its production.",
    cta: "Explore upstream →",
    to: "/#contact",
    disabled: true,
  },
];

const INDUSTRIES = ["Midstream", "Upstream", "Energy transition", "Other"];

const FOOTER_NAV = [
  { label: "Midstream", to: "/midstream" },
  { label: "Contacts", to: "/#contact" },
];

const emptyForm = {
  firstName: "",
  lastName: "",
  designation: "",
  organization: "",
  email: "",
  industry: INDUSTRIES[0],
};

const REQUIRED = ["firstName", "lastName", "designation", "organization", "email"];

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Stagger index for entrance animations (read as var(--d) in the SCSS)
const d = (i) => ({ "--d": i });

const ArrowIcon = () => (
  <img src={`${IMG}/arrow-right.svg`} alt="" width="13" height="13" />
);

const TopNav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="hv2-nav">
      <Link to="/" className="hv2-nav__brand" aria-label="Dsider home">
        <img
          className="hv2-logo"
          src={`${IMG}/logo-nav.svg`}
          alt="Dsider"
          width="155"
          height="32"
        />
        <span className="hv2-nav__divider" aria-hidden="true" />
      </Link>
      <nav
        className={`hv2-nav__links${open ? " is-open" : ""}`}
        aria-label="Primary"
      >
        {NAV_LINKS.map((l) =>
          l.disabled ? (
            <span key={l.label} className="is-disabled" aria-disabled="true">
              {l.label}
            </span>
          ) : (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)}>
              {l.label}
            </Link>
          )
        )}
      </nav>
      <div className="hv2-nav__actions">
        <Link to="/#contact" className="hv2-btn hv2-btn--primary hv2-btn--sm">
          Talk to our team
        </Link>
        <button
          type="button"
          className="hv2-nav__toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  );
};

const Hero = () => (
  <section className="hv2-hero">
    <div className="hv2-hero__inner">
      <div className="hv2-hero__copy">
        <span className="hv2-pill hv2-hero-in" style={d(0)}>
          <i aria-hidden="true" />
          Decision intelligence for energy
        </span>
        <h1 className="hv2-hero-in" style={d(1)}>
          Decisions grounded in <em>physics and economics.</em>
        </h1>
        <p className="hv2-hero__sub hv2-hero-in" style={d(2)}>
          Dsider follows the molecule and puts a dollar value on every choice
          along the way, so your team can make the call, show the math, and
          stand behind it.
        </p>
        <div className="hv2-hero__actions hv2-hero-in" style={d(3)}>
          <Link to="/#contact" className="hv2-btn hv2-btn--primary">
            Talk to our team <ArrowIcon />
          </Link>
          <Link to="/midstream" className="hv2-btn hv2-btn--ghost">
            For midstream operators →
          </Link>
        </div>
      </div>
      <div className="hv2-hero__visual hv2-hero-in hv2-from-right" style={d(2)}>
        <img
          src={`${IMG}/hero-midstream.png`}
          alt="Isometric illustration of oil and gas production, refining, offshore and fuel distribution"
        />
      </div>
    </div>
  </section>
);

const WhySection = () => (
  <section className="hv2-section hv2-section--why">
    <div className="hv2-container">
      <div className="hv2-why__head">
        <div className="hv2-why__intro">
          <span className="hv2-eyebrow hv2-reveal">Why Dsider</span>
          <h2 className="hv2-reveal" style={d(1)}>
            One model, <em>from source to settlement.</em>
          </h2>
          <p className="hv2-lead hv2-reveal" style={d(2)}>
            Dsider combines first-principles simulation with your contracts,
            prices, and costs, so engineering, finance, and leadership work from
            the same numbers.
          </p>
        </div>
        <div className="hv2-soc hv2-reveal" style={d(2)}>
          <img
            src={`${IMG}/soc2-seal.png`}
            alt="AICPA SOC 2"
            width="126"
            height="125"
          />
          <p>Dsider is SOC 2 Type 1 compliant.</p>
        </div>
      </div>

      <ol className="hv2-journey hv2-reveal" style={d(3)} aria-label="From wellhead to settlement">
        {JOURNEY.map((step, i) => (
          <li key={step.label} className="hv2-journey__step" style={d(i)}>
            <div className="hv2-journey__node">
              <span className={`hv2-journey__dot hv2-journey__dot--${step.tone}`}>
                <i />
              </span>
              <span className="hv2-journey__label">{step.label}</span>
            </div>
            {step.line && (
              <span
                className={`hv2-journey__line hv2-journey__line--${step.line}`}
                aria-hidden="true"
              />
            )}
          </li>
        ))}
      </ol>

      <div className="hv2-shot hv2-shot--optimizer hv2-reveal">
        <img
          src={`${IMG}/decisions.png`}
          alt="Decision view in Dsider"
        />
      </div>

      <div className="hv2-grid">
        {PILLARS.map((p, i) => (
          <article
            key={p.title}
            className="hv2-card hv2-card--dark hv2-reveal"
            style={d(i)}
          >
            <span className="hv2-card__icon">
              <img src={`${IMG}/${p.icon}`} alt="" width={p.width} height={p.height} />
            </span>
            <h3 style={{ maxWidth: p.titleWidth }}>{p.title}</h3>
            <p>{p.body}</p>
          </article>
        ))}
      </div>

      <p className="hv2-trust hv2-reveal">
        <i aria-hidden="true" />
        Dsider is SOC 2 Type 1 compliant.
      </p>
    </div>
  </section>
);

const PathsSection = () => (
  <section
    id="choose-your-path"
    className="hv2-section hv2-section--paths"
  >
    <div className="hv2-container">
      <span className="hv2-eyebrow hv2-reveal">Choose Your Path</span>
      <h2 className="hv2-reveal" style={d(1)}>
        Start where <em>your decisions are.</em>
      </h2>
      <p className="hv2-lead hv2-reveal" style={d(2)}>
        Whether you move molecules, produce them, or are building what comes
        next, Dsider models the decisions in front of you. Pick the path that
        fits your work.
      </p>
      <div className="hv2-grid hv2-grid--paths">
        {PATHS.map((p, i) => (
          <article
            key={p.title}
            className="hv2-card hv2-card--path hv2-reveal"
            style={d(i + 1)}
          >
            <h3>{p.title}</h3>
            <div className={`hv2-shot hv2-shot--path ${p.imageClass || ""}`}>
              <img src={`${IMG}/${p.image}`} alt="" />
            </div>
            <p>{p.body}</p>
            {p.disabled ? (
              <button
                type="button"
                className="hv2-btn hv2-btn--ghost"
                disabled
                aria-disabled="true"
              >
                {p.cta}
              </button>
            ) : (
              <Link
                to={p.to}
                className={`hv2-btn ${p.primary ? "hv2-btn--primary" : "hv2-btn--ghost"}`}
              >
                {p.cta}
                {p.primary && <ArrowIcon />}
              </Link>
            )}
          </article>
        ))}
      </div>
    </div>
  </section>
);

const ContactSection = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: validateField(name, value) });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (REQUIRED.includes(name)) {
      setErrors({ ...errors, [name]: validateField(name, value) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = REQUIRED.reduce((acc, key) => {
      const error = validateField(key, formData[key]);
      if (error) acc[key] = error;
      return acc;
    }, {});
    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      setStatus({ type: "", text: "" });
      const first = e.target.querySelector(`[name="${Object.keys(formErrors)[0]}"]`);
      if (first) first.focus();
      return;
    }

    const cleaned = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, v.trim()])
    );
    setSending(true);
    try {
      // Industry has no dedicated HubSpot property yet, so it rides in the message.
      await submitToHubspot({
        ...cleaned,
        message: `Industry: ${cleaned.industry}`,
      });
      setStatus({ type: "success", text: "Thanks — we'll be in touch shortly." });
      setFormData(emptyForm);
      setErrors({});
    } catch (error) {
      console.error("Error making post request:", error.message);
      setStatus({
        type: "error",
        text: "There was a problem sending your request, please try again later.",
      });
    }
    setSending(false);
  };

  const field = (name, label, props = {}) => (
    <label className="hv2-field">
      <span>{label}</span>
      <input
        name={name}
        value={formData[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={100}
        required
        aria-invalid={errors[name] ? "true" : "false"}
        {...props}
      />
      {errors[name] && (
        <small className="hv2-field__error" role="alert">
          {errors[name]}
        </small>
      )}
    </label>
  );

  return (
    <section id="contact" className="hv2-section hv2-section--cta">
      <div className="hv2-cta">
        <span className="hv2-eyebrow hv2-eyebrow--center hv2-reveal">Contact</span>
        <h2 className="hv2-reveal" style={d(1)}>Have a decision you'd like to see in dollars?</h2>
        <p className="hv2-cta__sub hv2-reveal" style={d(2)}>
          Tell us about it. We'll show you how Dsider would model it.
        </p>
        <form className="hv2-form hv2-reveal" style={d(3)} onSubmit={handleSubmit} noValidate>
          <div className="hv2-form__row">
            {field("firstName", "First Name *", {
              type: "text",
              placeholder: "First Name",
              autoComplete: "given-name",
            })}
            {field("lastName", "Last Name *", {
              type: "text",
              placeholder: "Last Name",
              autoComplete: "family-name",
            })}
          </div>
          <div className="hv2-form__row">
            {field("designation", "Title *", {
              type: "text",
              placeholder: "VP Operations, CFO, ...",
              autoComplete: "organization-title",
            })}
            {field("organization", "Company *", {
              type: "text",
              placeholder: "Your Company",
              autoComplete: "organization",
            })}
          </div>
          {field("email", "Work email *", {
            type: "email",
            placeholder: "you@company.com",
            autoComplete: "email",
          })}
          <label className="hv2-field">
            <span>Industry</span>
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
            >
              {INDUSTRIES.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </label>
          {status.text && (
            <p className={`hv2-form__status hv2-form__status--${status.type}`} role="status">
              {status.text}
            </p>
          )}
          <button
            type="submit"
            className="hv2-btn hv2-btn--primary hv2-btn--block"
            disabled={sending}
          >
            {sending ? "Sending..." : "Talk to our team"}
            {!sending && <ArrowIcon />}
          </button>
          <p className="hv2-form__alt">
            Or email <a href="mailto:info@dsider.app">info@dsider.app</a>
          </p>
        </form>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="hv2-footer">
    <div className="hv2-container">
      <div className="hv2-footer__top">
        <div className="hv2-footer__brand">
          <Link to="/" aria-label="Dsider home">
            <img
              className="hv2-logo"
              src={`${IMG}/logo-footer.svg`}
              alt="Dsider"
              width="155"
              height="32"
            />
          </Link>
          <p>Decisions grounded in physics and economics.</p>
          <p>
            We're hiring. <span className="hv2-footer__disabled" aria-disabled="true">
              See open roles →
            </span>
          </p>
        </div>
        <div className="hv2-footer__cols">
          <div>
            <h4>Contact</h4>
            <a href="mailto:contact@dsider.app">contact@dsider.app</a>
            <span>Houston, Texas</span>
          </div>
          <div>
            <h4>Navigation</h4>
            {FOOTER_NAV.map((l) => (
              <Link key={l.label} to={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <div className="hv2-footer__bottom">
        <span>© 2026 Dsider Inc. All rights reserved.</span>
        <span>Dsider · Grounded in Physics and economics</span>
      </div>
    </div>
  </footer>
);

const HomeV2 = () => {
  const { hash } = useLocation();
  const rootRef = useRef(null);

  // Reveal .hv2-reveal elements once as they scroll into view
  useEffect(() => {
    const items = rootRef.current.querySelectorAll(".hv2-reveal");
    if (!("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hash) return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (el)
        el.scrollIntoView({
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
    }, 100);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
  <div className="hv2" ref={rootRef}>
    <Helmet>
      <title>Dsider | Decisions Grounded in Physics and Economics</title>
      <meta
        name="description"
        content="Dsider follows the molecule from source to settlement and puts a dollar value on every decision along the way."
      />
    </Helmet>
    <TopNav />
    <main>
      <Hero />
      <WhySection />
      <PathsSection />
      <ContactSection />
    </main>
    <Footer />
  </div>
  );
};

export default HomeV2;
